import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { updateNStats } from "../app/slices/userSlice";
import { apiUrl } from "../utils/backendUrl";

const dropdownRequests = {
  notifications: () =>
    axios
      .get(apiUrl("/api/v1/notifications?page=1&limit=5"), {
        withCredentials: true,
      })
      .then((response) => response.data?.data?.notifications || []),
  friends: () =>
    axios
      .get(apiUrl("/api/v1/friends/requests?page=1&limit=5"), {
        withCredentials: true,
      })
      .then((response) => response.data?.data?.requests || []),
  messages: () =>
    axios
      .get(apiUrl("/api/v1/messages/conversations"), {
        withCredentials: true,
      })
      .then((response) =>
        (response.data?.data?.conversations || []).slice(0, 5),
      ),
};

export default function useAuthenticatedDropdowns({ user, dispatch }) {
  const [notiItems, setNotiItems] = useState(null);
  const [frReqItems, setFrReqItems] = useState(null);
  const [msgItems, setMsgItems] = useState(null);

  const setters = {
    notifications: setNotiItems,
    friends: setFrReqItems,
    messages: setMsgItems,
  };

  useEffect(() => {
    if (!user.userinfo) return;

    Object.entries(dropdownRequests).forEach(([type, request]) => {
      request()
        .then(setters[type])
        .catch(() => setters[type]([]));
    });
    // The authenticated user identity is the only input that should reload all menus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userinfo]);

  const handleOpenBox = useCallback(async (type) => {
    const request = dropdownRequests[type];
    const setItems = setters[type];
    if (!request || !setItems) return;

    try {
      setItems(await request());
    } catch {
      // Dropdown refresh is best-effort; retain the last successful data.
    }
    // Setters are stable React state functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFriendRequest = useCallback(
    (senderId) => {
      setFrReqItems((previous) =>
        (previous || []).filter((request) => {
          const sender = request.requester || request.sender || {};
          return String(sender._id) !== String(senderId);
        }),
      );
      const currentCount = user.userinfo?.recivedRequestsCount || 0;
      dispatch(
        updateNStats({
          recivedRequestsCount: Math.max(0, currentCount - 1),
        }),
      );
    },
    [dispatch, user.userinfo?.recivedRequestsCount],
  );

  const handleAcceptRequest = useCallback(
    async (senderId) => {
      try {
        await axios.post(
          apiUrl(`/api/v1/friends/accept/${senderId}`),
          {},
          { withCredentials: true },
        );
        removeFriendRequest(senderId);
        toast.success("Friend request accepted");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to accept request",
        );
      }
    },
    [removeFriendRequest],
  );

  const handleRejectRequest = useCallback(
    async (senderId) => {
      try {
        await axios.delete(apiUrl(`/api/v1/friends/reject/${senderId}`), {
          withCredentials: true,
        });
        removeFriendRequest(senderId);
        toast.success("Friend request rejected");
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Failed to reject request",
        );
      }
    },
    [removeFriendRequest],
  );

  return {
    notiItems,
    frReqItems,
    msgItems,
    handleOpenBox,
    handleAcceptRequest,
    handleRejectRequest,
  };
}
